/**
 * Browser-side video exporter for the Creator Studio.
 *
 * Strategy:
 *   1. Animate a Canvas frame-by-frame (text fade-in, gentle gradient drift).
 *   2. canvas.captureStream() → MediaStream video track.
 *   3. Web Audio destination (bg music + SFX) → MediaStream audio track.
 *   4. MediaRecorder records both into a WebM blob.
 *   5. @ffmpeg/ffmpeg (lazy-loaded) transcodes WebM → MP4 (H.264 + AAC).
 *
 * Browser TTS is NOT included in the MP4 (speechSynthesis bypasses WebAudio
 * and cannot be captured). For voice-over baked into the file, swap to
 * OpenAI TTS in a later round.
 */

let ffmpegInstance = null;
let ffmpegLoadPromise = null;

async function loadFfmpeg(onProgress) {
  if (ffmpegInstance) return ffmpegInstance;
  if (ffmpegLoadPromise) return ffmpegLoadPromise;
  ffmpegLoadPromise = (async () => {
    const { FFmpeg } = await import("@ffmpeg/ffmpeg");
    const { toBlobURL } = await import("@ffmpeg/util");
    const ff = new FFmpeg();
    if (onProgress) {
      ff.on("progress", ({ progress }) => onProgress(Math.min(100, Math.round(progress * 100))));
      ff.on("log", ({ message }) => { /* console.debug(message); */ });
    }
    const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd";
    await ff.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
    });
    ffmpegInstance = ff;
    return ff;
  })();
  return ffmpegLoadPromise;
}

/**
 * Pick the best MIME type the browser will record into.
 */
function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  const types = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=h264,opus",
    "video/webm",
    "video/mp4",
  ];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

/**
 * Recorder options:
 *   - canvas:   HTMLCanvasElement (already drawn — we re-draw each frame via drawFrame())
 *   - duration: seconds (3-15 typical)
 *   - drawFrame: (canvas, elapsedSec, totalSec) => void — called every frame
 *   - audioUrl:  optional URL for background music
 *   - voiceBuffer: optional Web Audio AudioBuffer (decoded TTS narration); mixed
 *                  on top of bg music at +6 dB
 *   - onProgress: ({ phase, percent }) => void
 *   - format: "mp4" | "webm" — "webm" skips the ffmpeg transcode (5–10× faster).
 *             Use "webm" when uploading to Mux (which re-encodes server-side),
 *             "mp4" for local download / iOS Photos compatibility.
 */
export async function recordVideo({
  canvas,
  duration = 8,
  drawFrame,
  audioUrl = null,
  voiceBuffer = null,
  fps = 30,
  format = "mp4",
  onProgress,
}) {
  if (!canvas || typeof canvas.captureStream !== "function") {
    throw new Error("Canvas does not support captureStream");
  }
  const mime = pickMimeType();
  if (!mime) throw new Error("MediaRecorder not supported in this browser");

  // Video stream from canvas
  const stream = canvas.captureStream(fps);

  // Audio: background music + optional TTS voice-over, both mixed through a
  // MediaStreamDestination so MediaRecorder captures them. If NEITHER is
  // provided we still attach a silent AudioContext track — otherwise Mux
  // ends up producing a video-only HLS manifest that HLS.js rejects with
  // 'manifestIncompatibleCodecsError' in Chromium.
  let audioContext = null;
  let audioSource = null;
  let voiceSource = null;
  let silenceOsc = null;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    audioContext = new AC();
    const dest = audioContext.createMediaStreamDestination();
    if (audioUrl) {
      const res = await fetch(audioUrl);
      const buf = await res.arrayBuffer();
      const decoded = await audioContext.decodeAudioData(buf);
      audioSource = audioContext.createBufferSource();
      audioSource.buffer = decoded;
      audioSource.loop = decoded.duration < duration;
      const musicGain = audioContext.createGain();
      // Duck music when voice is present so narration cuts through
      musicGain.gain.value = voiceBuffer ? 0.35 : 0.85;
      audioSource.connect(musicGain).connect(dest);
    }
    if (voiceBuffer) {
      voiceSource = audioContext.createBufferSource();
      voiceSource.buffer = voiceBuffer;
      const voiceGain = audioContext.createGain();
      voiceGain.gain.value = 1.4;
      voiceSource.connect(voiceGain).connect(dest);
    }
    if (!audioUrl && !voiceBuffer) {
      // Silent oscillator at 0 gain — inaudible but produces a valid audio
      // track so Mux HLS manifests always include an audio group.
      silenceOsc = audioContext.createOscillator();
      const silenceGain = audioContext.createGain();
      silenceGain.gain.value = 0.0001;
      silenceOsc.connect(silenceGain).connect(dest);
    }
    dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
    audioSource?.start();
    voiceSource?.start();
    silenceOsc?.start();
  } catch (e) {
    console.warn("[videoExporter] Audio mix failed:", e?.message);
    audioContext?.close().catch(() => {});
    audioContext = null;
  }

  // Start the animation loop (draws into the canvas at ~fps)
  const startTs = performance.now();
  let raf = null;
  const tick = () => {
    const elapsed = (performance.now() - startTs) / 1000;
    if (elapsed >= duration) return;
    try { drawFrame?.(canvas, elapsed, duration); } catch (e) { /* ignore */ }
    raf = requestAnimationFrame(tick);
  };
  tick();

  // Record
  onProgress?.({ phase: "recording", percent: 0 });
  const chunks = [];
  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

  const recordPromise = new Promise((resolve, reject) => {
    recorder.onstop = () => resolve();
    recorder.onerror = (e) => reject(e.error || new Error("Recorder error"));
  });

  recorder.start();
  // Progress ticks while recording
  const progressTimer = setInterval(() => {
    const elapsed = (performance.now() - startTs) / 1000;
    onProgress?.({ phase: "recording", percent: Math.min(100, Math.round((elapsed / duration) * 100)) });
  }, 200);

  await new Promise((res) => setTimeout(res, duration * 1000));
  recorder.stop();
  clearInterval(progressTimer);
  if (raf) cancelAnimationFrame(raf);
  await recordPromise;

  try { audioSource?.stop(); } catch (e) { /* ignore */ }
  try { voiceSource?.stop(); } catch (e) { /* ignore */ }
  try { silenceOsc?.stop(); } catch (e) { /* ignore */ }
  try { audioContext?.close(); } catch (e) { /* ignore */ }
  stream.getTracks().forEach((t) => t.stop());

  const webmBlob = new Blob(chunks, { type: mime || "video/webm" });

  // Fast path: caller wants the raw WebM (Mux accepts WebM directly).
  if (format === "webm") {
    onProgress?.({ phase: "done", percent: 100 });
    return webmBlob;
  }

  onProgress?.({ phase: "encoding", percent: 0 });

  // Transcode WebM → MP4 with H.264 + AAC for max compatibility (Mux, iOS Safari)
  const ff = await loadFfmpeg((pct) => onProgress?.({ phase: "encoding", percent: pct }));
  const inputName = "in.webm";
  const outputName = "out.mp4";
  const webmBytes = new Uint8Array(await webmBlob.arrayBuffer());
  await ff.writeFile(inputName, webmBytes);
  await ff.exec([
    "-i", inputName,
    "-c:v", "libx264",
    "-preset", "ultrafast",
    "-crf", "23",
    "-pix_fmt", "yuv420p",
    "-c:a", "aac",
    "-b:a", "128k",
    "-movflags", "+faststart",
    outputName,
  ]);
  const mp4Data = await ff.readFile(outputName);
  // Clean up FS
  try { await ff.deleteFile(inputName); await ff.deleteFile(outputName); } catch (e) { /* ignore */ }

  const mp4Blob = new Blob([mp4Data.buffer], { type: "video/mp4" });
  onProgress?.({ phase: "done", percent: 100 });
  return mp4Blob;
}
